import React, { useEffect } from 'react';
import {
    CheckCircle2,
    XCircle,
    Rocket,
    Target,
    TrendingUp,
    Layers,
    Sparkles,
    BarChart3,
    HeartHandshake,
    Compass,
    Calendar,
    Workflow,
    Phone,
    AlertTriangle,
    Filter,
    Users,
    Eye,
    ArrowUpRight,
    IndianRupee,
    ShieldCheck,
    Clock
} from 'lucide-react';

/* Reusable Primitives */

const SectionTag = ({ icon: IconComp, label, color = "indigo" }) => {
    const colorClasses = {
        indigo: "bg-indigo-500/10 border-indigo-500/20 text-indigo-300",
        rose: "bg-rose-500/10 border-rose-500/20 text-rose-300",
        emerald: "bg-emerald-500/10 border-emerald-500/20 text-emerald-300",
        amber: "bg-amber-500/10 border-amber-500/20 text-amber-300",
        purple: "bg-purple-500/10 border-purple-500/20 text-purple-300"
    };
    return (
        <div className={`inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full border text-xs sm:text-sm font-semibold tracking-wide uppercase ${colorClasses[color]}`}>
            <IconComp className="w-4 h-4" /> {label}
        </div>
    );
};

const StatCard = ({ value, label, sub, accent = "indigo" }) => {
    const colors = {
        indigo: "from-indigo-900/40 to-slate-900/80 border-indigo-500/30",
        emerald: "from-emerald-900/40 to-slate-900/80 border-emerald-500/30",
        purple: "from-purple-900/40 to-slate-900/80 border-purple-500/30",
        amber: "from-amber-900/40 to-slate-900/80 border-amber-500/30",
        rose: "from-rose-900/40 to-slate-900/80 border-rose-500/30"
    };
    return (
        <div className={`bg-gradient-to-br ${colors[accent]} border rounded-2xl p-5 sm:p-6 text-center h-full flex flex-col justify-center`}>
            <div className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-1">{value}</div>
            <div className="text-xs sm:text-sm font-semibold text-white/80">{label}</div>
            {sub && <div className="text-[10px] sm:text-xs text-white/50 mt-2">{sub}</div>}
        </div>
    );
};

const MiniCard = ({ icon: IconComp, title, sub, color = "indigo" }) => {
    const ring = {
        indigo: "text-indigo-300",
        emerald: "text-emerald-300",
        purple: "text-purple-300",
        amber: "text-amber-300",
        rose: "text-rose-300"
    };
    return (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6 h-full">
            <IconComp className={`w-6 h-6 mb-3 ${ring[color]}`} />
            <h3 className="text-white font-bold text-base sm:text-lg mb-1 leading-snug">{title}</h3>
            <p className="text-slate-400 text-sm leading-snug">{sub}</p>
        </div>
    );
};

const Check = ({ children }) => (
    <li className="flex gap-3 items-start">
        <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-400" />
        <span className="leading-snug text-slate-200 text-sm">{children}</span>
    </li>
);

const SectionHead = ({ icon, tag, color, title, sub }) => (
    <div className="text-center mb-10">
        <SectionTag icon={icon} label={tag} color={color} />
        <h2 className="text-3xl sm:text-4xl font-black mb-3">{title}</h2>
        {sub && <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">{sub}</p>}
    </div>
);

/* Main Component */
export default function DebmitaPersonalBrand() {

    useEffect(() => {
        document.title = "Debmita Dutta | Personal Brand Proposal";
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-20 pt-24 relative z-10">

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">Debmita PB</span>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-lg border border-indigo-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-indigo-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">Proposal</span>
                </div>
            </header>

            <main className="max-w-[1120px] mx-auto px-4 space-y-20 sm:space-y-28">

                {/* HERO */}
                <section className="text-center pt-6 sm:pt-10">
                    <SectionTag icon={Rocket} label="Founder-Led Content System" />
                    <h1 className="text-4xl sm:text-5xl md:text-7xl font-black tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-tight pb-2">
                        Your job is to record.<br />Everything else is us.
                    </h1>
                    <p className="text-base sm:text-xl md:text-2xl text-white/60 max-w-3xl mx-auto leading-relaxed mb-10">
                        We turn your reach from luck into a system, and scale it 10x in 6 months.
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-4 max-w-3xl mx-auto gap-3 sm:gap-4 mt-10">
                        <StatCard value="10x" label="Reach in 6 months" sub="~430K → ~4.3M plays/mo" accent="indigo" />
                        <StatCard value="30" label="Reels / month" sub="up from ~14 today" accent="emerald" />
                        <StatCard value="299K" label="Followers you reach ~3% of" sub="the gap we close" accent="amber" />
                        <StatCard value="A system" label="What we build" sub="reach on purpose, not luck" accent="purple" />
                    </div>
                </section>

                {/* PAIN — WHERE YOU ARE TODAY */}
                <section>
                    <SectionHead
                        icon={Eye} tag="Where you are today" color="amber"
                        title="Your reach didn't dip. It collapsed."
                        sub="Same 299K audience the whole time. Your median reel went from ~130K reach in early 2025 to under 10K today."
                    />

                    {/* then vs now */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 max-w-5xl mx-auto mb-10">
                        <StatCard value="~130K" label="Median reach / reel" sub="early 2025" accent="emerald" />
                        <StatCard value="<10K" label="Median reach / reel" sub="today" accent="rose" />
                        <StatCard value="~93%" label="Drop in reach" sub="audience never shrank" accent="rose" />
                        <StatCard value="299K" label="Followers" sub="unchanged throughout" accent="amber" />
                    </div>

                    {/* decline curve — median reach per reel */}
                    <div className="max-w-4xl mx-auto rounded-2xl border border-white/10 bg-slate-900/40 backdrop-blur p-5 sm:p-7 mb-8">
                        <div className="text-center mb-5">
                            <h3 className="text-white font-bold text-base sm:text-lg">Your median reel: reach per post</h3>
                            <p className="text-slate-400 text-xs sm:text-sm">Month by month, from your public play counts</p>
                        </div>
                        <svg viewBox="0 0 800 300" className="w-full h-auto" role="img" aria-label="Median reach per reel collapsing from about 138 thousand in January 2025 to under 10 thousand today">
                            <defs>
                                <linearGradient id="declineGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="rgba(244,63,94,0.35)" />
                                    <stop offset="100%" stopColor="rgba(244,63,94,0)" />
                                </linearGradient>
                            </defs>
                            <line x1="60" y1="250" x2="780" y2="250" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
                            <path d="M60,186 L125,43 L190,54 L255,198 L320,163 L385,154 L450,211 L515,226 L580,231 L580,250 L60,250 Z" fill="url(#declineGrad)" />
                            <polyline points="60,186 125,43 190,54 255,198 320,163 385,154 450,211 515,226 580,231" fill="none" stroke="#f43f5e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                            <line x1="580" y1="231" x2="690" y2="236" stroke="#fb7185" strokeWidth="2.5" strokeDasharray="5 5" opacity="0.7" />
                            <polyline points="690,236 745,236" fill="none" stroke="#f43f5e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                            <text x="635" y="225" textAnchor="middle" fill="#94a3b8" fontSize="10">Sep ’25 – Mar ’26 not shown</text>
                            <circle cx="125" cy="43" r="5" fill="#fb7185" />
                            <text x="135" y="36" textAnchor="start" fill="#ffffff" fontSize="13" fontWeight="800">Jan ’25 · ~138K</text>
                            <circle cx="745" cy="236" r="5" fill="#f43f5e" />
                            <text x="745" y="228" textAnchor="end" fill="#fecdd3" fontSize="13" fontWeight="800">today · ~9.5K</text>
                            {[["Dec ’24", 60], ["Jun ’25", 450], ["Aug ’25", 580], ["now", 745]].map(([lbl, x], i) => (
                                <text key={i} x={x} y="272" textAnchor="middle" fill="#94a3b8" fontSize="12">{lbl}</text>
                            ))}
                        </svg>
                    </div>

                    {/* both metrics: peak / mid / now */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto">
                        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-900/10 p-5 sm:p-6">
                            <div className="text-emerald-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-3">Early 2025 · the peak</div>
                            <div className="flex justify-between text-sm py-1 border-b border-white/5"><span className="text-slate-300">Median / reel</span><span className="text-white font-bold">~130K</span></div>
                            <div className="flex justify-between text-sm py-1"><span className="text-slate-300">Total plays / mo</span><span className="text-white font-bold">~11.8M</span></div>
                        </div>
                        <div className="rounded-2xl border border-amber-500/30 bg-amber-900/10 p-5 sm:p-6">
                            <div className="text-amber-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-3">Mid 2025 · sliding</div>
                            <div className="flex justify-between text-sm py-1 border-b border-white/5"><span className="text-slate-300">Median / reel</span><span className="text-white font-bold">~15K</span></div>
                            <div className="flex justify-between text-sm py-1"><span className="text-slate-300">Total plays / mo</span><span className="text-white font-bold">~1M</span></div>
                        </div>
                        <div className="rounded-2xl border-2 border-rose-500/40 bg-rose-900/15 p-5 sm:p-6">
                            <div className="text-rose-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-3">Today · the floor</div>
                            <div className="flex justify-between text-sm py-1 border-b border-white/5"><span className="text-slate-300">Median / reel</span><span className="text-white font-bold">&lt;10K</span></div>
                            <div className="flex justify-between text-sm py-1"><span className="text-slate-300">Total plays / mo</span><span className="text-white font-bold">~0.4M</span></div>
                        </div>
                    </div>

                    <p className="text-slate-500 text-xs text-center max-w-3xl mx-auto mt-5">From your public play counts. Older reels' counts are lifetime totals, so we lead with median reach per reel. The early-2025 spike was luck, not a system, which is why it didn't hold.</p>
                </section>

                {/* PROOF — WE DID THE HOMEWORK */}
                <section>
                    <SectionHead
                        icon={Users} tag="We did the homework" color="indigo"
                        title="We understand who you are talking to"
                        sub="Your own data already shows what your audience rewards."
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 max-w-5xl mx-auto">
                        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-900/10 p-6 sm:p-8">
                            <h3 className="text-base sm:text-lg font-bold text-white mb-4">Your biggest reels, one DNA</h3>
                            <ul className="space-y-3 text-sm">
                                <li className="flex justify-between gap-3 border-b border-white/5 pb-3"><span className="text-slate-200">"If your child hits, it does NOT mean a bad child"</span><span className="text-emerald-300 font-bold whitespace-nowrap">681K</span></li>
                                <li className="flex justify-between gap-3 border-b border-white/5 pb-3"><span className="text-slate-200">"Stop teaching ABCD and 1234 too early"</span><span className="text-emerald-300 font-bold whitespace-nowrap">254K</span></li>
                                <li className="flex justify-between gap-3"><span className="text-slate-200">"Disobedience is a sign of development"</span><span className="text-emerald-300 font-bold whitespace-nowrap">21K</span></li>
                            </ul>
                            <p className="text-slate-400 text-xs mt-4">Counterintuitive neuroscience hooks win. LIVEs and announcements die under 2.5K.</p>
                        </div>
                        <div className="rounded-2xl border border-indigo-500/30 bg-indigo-900/10 p-6 sm:p-8">
                            <h3 className="text-base sm:text-lg font-bold text-white mb-4">Who they are</h3>
                            <ul className="space-y-2.5">
                                <Check>Why-behind-the-should parents, not the spiritual lane</Check>
                                <Check>Anti-obedience, brain-development minded</Check>
                                <Check>Buyers who apply, not impulse-buy</Check>
                                <Check>Brand-trusted: Klay, Nestle, Accenture, top schools</Check>
                            </ul>
                        </div>
                    </div>
                </section>

                {/* THE SYSTEM */}
                <section>
                    <SectionHead
                        icon={Workflow} tag="The system we run" color="indigo"
                        title="A system, so reach stops being luck"
                        sub="Your job shrinks to recording. Everything around it is us."
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
                        <MiniCard icon={Target} color="indigo" title="Ideation + hooks" sub="Every reel starts from a proven-angle hook." />
                        <MiniCard icon={Sparkles} color="emerald" title="You just record" sub="Bullets in, you talk. We script, edit, caption, post." />
                        <MiniCard icon={Calendar} color="amber" title="Recording rhythm" sub="Prompts timed to the windows you actually have." />
                        <MiniCard icon={BarChart3} color="purple" title="Weekly dashboard" sub="Reach becomes a number we move, not a mystery." />
                    </div>
                </section>

                {/* SIX-MONTH ROADMAP */}
                <section>
                    <SectionHead
                        icon={TrendingUp} tag="The roadmap" color="indigo"
                        title="~430K to ~4.3M monthly reach in 6 months"
                        sub="A channel you own, scaled in phases. Each one unlocks the next."
                    />

                    {/* Growth curve */}
                    <div className="max-w-4xl mx-auto rounded-2xl border border-white/10 bg-slate-900/40 backdrop-blur p-5 sm:p-7 mb-10">
                        <svg viewBox="0 0 800 300" className="w-full h-auto" role="img" aria-label="Reach growth from about 430 thousand to about 4.3 million monthly plays over six months">
                            <defs>
                                <linearGradient id="reachGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="rgba(129,140,248,0.40)" />
                                    <stop offset="100%" stopColor="rgba(129,140,248,0)" />
                                </linearGradient>
                            </defs>
                            <line x1="60" y1="250" x2="780" y2="250" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
                            <path d="M60,229 L178,216 L297,201 L415,177 L533,138 L652,94 L770,40 L770,250 L60,250 Z" fill="url(#reachGrad)" />
                            <polyline points="60,229 178,216 297,201 415,177 533,138 652,94 770,40" fill="none" stroke="#818cf8" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                            <line x1="415" y1="72" x2="415" y2="250" stroke="rgba(168,85,247,0.5)" strokeWidth="1.5" strokeDasharray="5 4" />
                            <text x="415" y="64" textAnchor="middle" fill="#c4b5fd" fontSize="12" fontWeight="700">Fan pages switch on</text>
                            <circle cx="60" cy="229" r="4" fill="#818cf8" />
                            <circle cx="415" cy="177" r="4" fill="#c4b5fd" />
                            <circle cx="770" cy="40" r="5" fill="#a5b4fc" />
                            <text x="72" y="222" textAnchor="start" fill="#e2e8f0" fontSize="13" fontWeight="700">~430K/mo</text>
                            <text x="758" y="30" textAnchor="end" fill="#ffffff" fontSize="14" fontWeight="800">~4.3M/mo · 10x</text>
                            {[["Now", 60], ["M1", 178], ["M2", 297], ["M3", 415], ["M4", 533], ["M5", 652], ["M6", 770]].map(([lbl, x], i) => (
                                <text key={i} x={x} y="272" textAnchor="middle" fill="#94a3b8" fontSize="12">{lbl}</text>
                            ))}
                        </svg>
                    </div>

                    {/* Phase cards — minimal */}
                    <div className="max-w-4xl mx-auto space-y-3">
                        {[
                            { n: "1", m: "Months 1 to 2", chip: "~430K → ~1M/mo", t: "Foundation and rhythm", color: "indigo" },
                            { n: "2", m: "Month 3", chip: "~1.2 to 1.5M/mo", t: "Your page hits its stride, ~3x and predictable", color: "indigo" },
                            { n: "3", m: "Months 3 to 4", chip: "the multiplier", t: "Widening the mouth of the funnel: fan pages switch on", color: "purple" },
                            { n: "4", m: "Months 4 to 6", chip: "~4.3M/mo · 10x", t: "Compounding to 10x", color: "emerald", highlight: true },
                            { n: "6+", m: "Ongoing", chip: "levers you own", t: "More pages, more platforms, brand partnerships", color: "amber" }
                        ].map((p, i) => {
                            const border = p.highlight ? "border-2 border-emerald-500/40 bg-emerald-900/15" : "border border-white/10 bg-white/5";
                            const badge = {
                                indigo: "bg-indigo-500/20 border-indigo-500/40 text-indigo-200",
                                purple: "bg-purple-500/20 border-purple-500/40 text-purple-200",
                                emerald: "bg-emerald-500/20 border-emerald-500/40 text-emerald-200",
                                amber: "bg-amber-500/20 border-amber-500/40 text-amber-200"
                            }[p.color];
                            const chip = {
                                indigo: "bg-indigo-500/10 text-indigo-200",
                                purple: "bg-purple-500/10 text-purple-200",
                                emerald: "bg-emerald-500/15 text-emerald-200",
                                amber: "bg-amber-500/10 text-amber-200"
                            }[p.color];
                            return (
                                <div key={i} className={`rounded-2xl ${border} p-4 sm:p-5 flex items-center gap-4`}>
                                    <div className={`flex-shrink-0 w-10 h-10 rounded-full border flex items-center justify-center font-black text-sm ${badge}`}>{p.n}</div>
                                    <div className="flex-grow">
                                        <div className="flex flex-wrap items-center gap-2 mb-1">
                                            <span className="text-white/50 text-[10px] sm:text-xs font-bold uppercase tracking-widest">{p.m}</span>
                                            <span className={`text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-semibold ${chip}`}>{p.chip}</span>
                                        </div>
                                        <h3 className="text-white font-bold text-sm sm:text-base leading-snug">{p.t}</h3>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* PRICE UP AS YOU SCALE */}
                <section>
                    <SectionHead
                        icon={ArrowUpRight} tag="The compounding upside" color="emerald"
                        title="Reach goes up. So does your price."
                        sub="More reach plus sharper positioning equals pricing power."
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-5xl mx-auto">
                        <MiniCard icon={TrendingUp} color="emerald" title="Sharper positioning" sub="6 months of the right hooks compounds your authority." />
                        <MiniCard icon={ArrowUpRight} color="emerald" title="Anchor higher" sub="Your ₹2.4L cohort can price up as demand grows." />
                        <MiniCard icon={Filter} color="emerald" title="A ladder below" sub="A smaller offer catches families not yet ready." />
                    </div>
                </section>

                {/* THREE HARD LINES */}
                <section>
                    <SectionHead
                        icon={AlertTriangle} tag="The hard lines" color="rose"
                        title="Three things we will not do"
                        sub="Drawn straight from your June 1 call."
                    />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto">
                        <MiniCard icon={XCircle} color="rose" title="No synthetic delivery" sub="Your face, your voice, your room. No AI clone." />
                        <MiniCard icon={XCircle} color="rose" title="No template, off-the-shelf look" sub="We raise the production quality while keeping it authentically you, premium enough for high-ticket parents." />
                        <MiniCard icon={XCircle} color="rose" title="No virality-chasing" sub="We optimize for the right parents, not the algorithm." />
                    </div>
                </section>

                {/* WHAT WE BUILD */}
                <section>
                    <SectionHead
                        icon={BarChart3} tag="What we're building" color="emerald"
                        title="A system you own, not a lucky streak"
                        sub="Success is reach you can repeat on purpose, week after week."
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
                        <div className="rounded-2xl border-2 border-emerald-500/40 bg-emerald-900/15 p-6 sm:p-7">
                            <div className="flex items-center gap-2 mb-2"><Workflow className="w-5 h-5 text-emerald-300" /><span className="text-emerald-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest">The engine</span></div>
                            <h3 className="text-lg sm:text-xl font-black text-white">A repeatable hook library that compounds</h3>
                        </div>
                        <div className="rounded-2xl border-2 border-emerald-500/40 bg-emerald-900/15 p-6 sm:p-7">
                            <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-5 h-5 text-emerald-300" /><span className="text-emerald-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest">The outcome</span></div>
                            <h3 className="text-lg sm:text-xl font-black text-white">Reach that holds, not one-off spikes</h3>
                        </div>
                    </div>
                </section>

                {/* WHAT'S INCLUDED */}
                <section>
                    <SectionHead
                        icon={Layers} tag="What's included" color="indigo"
                        title="Everything inside the engine"
                        sub="Every month. No add-ons, no surprise upsells."
                    />
                    <div className="max-w-3xl mx-auto rounded-2xl border border-emerald-500/30 bg-emerald-900/10 p-6 sm:p-8">
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
                            <Check>30 reels a month across Instagram, YouTube Shorts, LinkedIn, Facebook, X and Threads</Check>
                            <Check>Ideation pipeline, seeded weekly</Check>
                            <Check>Hooks lifted from your podcasts and long-form</Check>
                            <Check>Scripting from your bullet points</Check>
                            <Check>Editing that stays authentic, raised in quality</Check>
                            <Check>Captions per platform, in your voice</Check>
                            <Check>Recording-habit coaching</Check>
                            <Check>Scheduling and posting</Check>
                            <Check>Weekly performance dashboard</Check>
                            <Check>Monthly direction review</Check>
                        </ul>
                    </div>
                </section>

                {/* INVESTMENT & ENGAGEMENT */}
                <section>
                    <SectionHead
                        icon={IndianRupee} tag="Investment" color="emerald"
                        title="Simple, all-inclusive pricing"
                        sub="One number a month. Every tool, every platform, and your whole team, included."
                    />

                    {/* Phase 1 + Phase 2 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
                        <div className="rounded-2xl border-2 border-emerald-500/40 bg-emerald-900/15 p-6 sm:p-8 flex flex-col">
                            <div className="text-emerald-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-4">Phase 1 · Months 1 to 3 · Your main pages</div>
                            <div className="flex items-end gap-2 mb-1">
                                <span className="text-4xl sm:text-5xl font-black text-white">₹1,50,000</span>
                                <span className="text-slate-400 text-sm mb-1.5">/ month</span>
                            </div>
                            <div className="text-slate-300 text-sm mb-4">+ 18% GST · <span className="text-white font-semibold">₹1,77,000 / month all-in</span></div>
                            <p className="text-slate-400 text-xs mb-5">Month-to-month, after a 3-month minimum commitment.</p>
                            <ul className="space-y-2.5 mt-auto">
                                <Check>30 reels a month, produced and posted for you</Check>
                                <Check>All your main pages: Instagram, YouTube Shorts, LinkedIn, Facebook, X and Threads</Check>
                                <Check>A dedicated editor, scriptwriter, social media manager and creative director</Check>
                                <Check>Four-level quality check, nothing posts without your approval</Check>
                            </ul>
                        </div>

                        <div className="rounded-2xl border border-purple-500/30 bg-purple-900/10 p-6 sm:p-8 flex flex-col">
                            <div className="text-purple-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-4">Phase 2 · Month 4 onward · Fan-page network</div>
                            <div className="flex items-end gap-2 mb-1">
                                <span className="text-4xl sm:text-5xl font-black text-white">~₹2,50,000</span>
                                <span className="text-slate-400 text-sm mb-1.5">/ month</span>
                            </div>
                            <div className="text-slate-300 text-sm mb-4">+ GST · <span className="text-white font-semibold">~₹2,95,000 / month all-in</span></div>
                            <p className="text-slate-400 text-xs mb-5">About ₹1,00,000 over Phase 1. We send a revised proposal at the end of Month 3 with the exact figure, set by how many fan pages we run.</p>
                            <ul className="space-y-2.5 mt-auto">
                                <Check>Everything in Phase 1, continued on your main pages</Check>
                                <Check>3 to 5 themed fan pages compounding reach back to your account</Check>
                                <Check>Your proven hooks multiplied, the exponential phase</Check>
                                <Check>Exact scope locked from your first quarter of data</Check>
                            </ul>
                        </div>
                    </div>

                    {/* Tools included */}
                    <div className="max-w-5xl mx-auto mt-5 rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-7">
                        <div className="flex items-center gap-2 mb-2">
                            <ShieldCheck className="w-5 h-5 text-emerald-300" />
                            <span className="text-emerald-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest">Tools, all included · ₹0 extra</span>
                        </div>
                        <p className="text-slate-300 text-sm leading-snug">Everything the system runs on sits inside the monthly fee: the Notion content desk and approval board, editing and asset tools, the multi-platform posting automation, analytics tracking, and the transcription tools we use to mine hooks from your old footage. No separate subscriptions, no surprise line items. The number you see is the number you pay.</p>
                    </div>

                    {/* Program starts on first reel */}
                    <div className="max-w-5xl mx-auto mt-5 rounded-2xl border-2 border-indigo-500/40 bg-indigo-900/15 p-6 sm:p-7">
                        <div className="flex items-start gap-3">
                            <Clock className="w-6 h-6 text-indigo-300 flex-shrink-0 mt-0.5" />
                            <div>
                                <h3 className="text-white font-bold text-base sm:text-lg mb-1">Your month starts when your first reel goes live, not on day one of onboarding.</h3>
                                <p className="text-slate-300 text-sm leading-snug">The 7-day onboarding sits outside the clock. We use that week to learn your voice, mine your footage and build your first buffer of reels. Week 1 of your reporting begins on your first published reel, so your Week 1 stats are real performance from real posts, and you get a full month of output for a full month's fee.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* WHAT WE NEED */}
                <section>
                    <SectionHead
                        icon={HeartHandshake} tag="What we'd need" color="indigo"
                        title="What we'd need from you"
                        sub="Six light things. Everything else is us."
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
                        <MiniCard icon={Phone} color="indigo" title="Recording time" sub="No studio. Phone, your room, the gaps you have." />
                        <MiniCard icon={Layers} color="indigo" title="Your old footage" sub="Podcasts and long-form, to mine for hooks." />
                        <MiniCard icon={Sparkles} color="indigo" title="Use your phrases" sub="Hooks come from your real words." />
                        <MiniCard icon={Calendar} color="indigo" title="Weekly 30-min call" sub="Whichever window works for you." />
                        <MiniCard icon={Compass} color="indigo" title="Monthly direction" sub="Approve what to push next." />
                        <MiniCard icon={Filter} color="indigo" title="Funnel access" sub="Read-only, so we can see what's working." />
                    </div>
                </section>

                {/* CLOSING */}
                <section className="text-center pb-10">
                    <div className="max-w-2xl mx-auto bg-gradient-to-br from-indigo-900/50 to-slate-900/80 border border-indigo-500/30 rounded-3xl p-8 sm:p-10 md:p-14 shadow-2xl">
                        <Rocket className="w-9 h-9 sm:w-10 sm:h-10 text-indigo-400 mx-auto mb-4" />
                        <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 text-white">Your job is to record. Everything else is us.</h2>
                        <p className="text-slate-300 text-base sm:text-lg mb-2">We build the system that turns your reach from luck into something you own.</p>
                        <p className="text-slate-400 text-sm">Suresh Malani · SocialSEO LLP</p>
                    </div>
                </section>

                {/* FOOTER */}
                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-8 border-t border-white/10">
                    <div className="font-bold text-white/70 mb-2">SocialSEO LLP</div>
                    <div className="mb-1">Delhi + Dubai</div>
                    <div className="mb-2">
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300 hover:text-indigo-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
